import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LogoMark } from "../common/BrandVisuals";
import { LangToggle } from "../common/LangToggle";
import { useLanguage } from "../../context/LanguageProvider";
import { useSiteData } from "../../context/SiteDataProvider";
import { pick, UI_STRINGS } from "../../lib/i18n";

const NAV = [
    { to: "/", key: "home", end: true },
    { to: "/a-propos", key: "about" },
    { to: "/domaines", key: "practice" },
    { to: "/fonctionnement", key: "process" },
    { to: "/blog", key: "blog" },
    { to: "/contact", key: "contact" },
];

export function Header() {
    const { lang } = useLanguage();
    const { siteContent } = useSiteData();
    const logoUrl = siteContent?.images?.logo_url || "";
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
                scrolled
                    ? "bg-ink/95 backdrop-blur border-b border-ink-border"
                    : "bg-ink border-b border-transparent"
            }`}
        >
            <div className="mx-auto max-w-7xl px-5 lg:px-10 h-20 flex items-center justify-between gap-6">
                <Link
                    to="/"
                    data-testid="header-home-link"
                    aria-label="Cabinet MABS — Accueil"
                    className="shrink-0"
                >
                    <LogoMark size="sm" src={logoUrl} />
                </Link>

                <nav
                    aria-label="Primary"
                    className="hidden lg:flex items-center gap-8"
                    data-testid="site-nav"
                >
                    {NAV.map((item) => (
                        <NavLink
                            key={item.key}
                            to={item.to}
                            end={item.end}
                            data-testid={`nav-${item.key}`}
                            className={({ isActive }) =>
                                `nav-link text-sm tracking-wide uppercase ${
                                    isActive ? "is-active" : ""
                                }`
                            }
                        >
                            {pick(UI_STRINGS.nav[item.key], lang)}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-4">
                    <LangToggle />
                    <Link
                        to="/contact#form"
                        data-testid="header-cta-book"
                        className="hidden md:inline-flex btn-gold"
                    >
                        {pick(UI_STRINGS.cta.book, lang)}
                    </Link>
                    <button
                        className="lg:hidden text-neutral-200 p-2"
                        aria-expanded={open}
                        aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
                        data-testid="mobile-menu-btn"
                        onClick={() => setOpen((v) => !v)}
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div
                    data-testid="mobile-nav"
                    className="lg:hidden bg-ink-deep border-t border-ink-border"
                >
                    <nav className="px-5 py-6 flex flex-col gap-4">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.key}
                                to={item.to}
                                end={item.end}
                                data-testid={`mobile-nav-${item.key}`}
                                className={({ isActive }) =>
                                    `text-base py-2 tracking-wide ${
                                        isActive
                                            ? "text-gold"
                                            : "text-neutral-200"
                                    }`
                                }
                            >
                                {pick(UI_STRINGS.nav[item.key], lang)}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact#form"
                            data-testid="mobile-cta-book"
                            className="btn-gold mt-3"
                        >
                            {pick(UI_STRINGS.cta.book, lang)}
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    );
}
